import toast from 'react-hot-toast';
import { AppError, ErrorSeverity, ErrorCode } from './types';
import { secureLog } from './logger';
import { ERROR_MESSAGES } from './messages';
import { sanitizeString } from '../../utils/sanitization';

const shownKeys = new Map<string, number>(); // key -> timestamp
const WINDOW_MS = 3000;

// Global flag to suppress all error notifications (useful for development/testing)
let suppressAllErrors = process.env.NODE_ENV === 'development';

// Function to enable/disable error suppression
export function setErrorSuppression(enabled: boolean) {
  suppressAllErrors = enabled;
}

export function notifyUser(e: AppError) {
  // Global suppression check
  if (suppressAllErrors) {
    // SECURITY: Removed console.warn to prevent information leakage
    return;
  }

  // Don't show toasts for transient errors - they should be silent and auto-retried
  if (e.code === 'TRANSIENT_BACKEND_UNAVAILABLE') {
    return;
  }

  // Suppress index errors on page load - these are development/admin issues
  if (e.code === 'DB/INDEX_REQUIRED') {
    // SECURITY: Removed console.warn to prevent information leakage
    return;
  }

  // Suppress permission errors - these are typically auth/security issues
  if (e.code === 'DB/PERMISSION_DENIED') {
    // SECURITY: Removed console.warn to prevent information leakage
    return;
  }

  // Suppress unknown errors that might be transient or non-critical
  if (e.code === 'UNKNOWN') {
    // In development, show more errors for debugging
    if (process.env.NODE_ENV === 'development') {
      // Only suppress very specific errors in development
      const message = e.message?.toLowerCase() || '';
      if (message.includes('index') || 
          message.includes('permission') || 
          message.includes('network') || 
          message.includes('unavailable') ||
          message.includes('timeout')) {
        secureLog('warn', 'Suppressed unknown error (development):', e.message);
        return;
      }
    } else {
      // In production, suppress most unknown errors as they're likely transient or non-critical
      // BUT: Don't suppress auth errors - users need to know if login fails
      const message = e.message?.toLowerCase() || '';
      
      // Don't suppress if it's an auth error (check domain or message)
      if (e.domain === 'auth' || message.includes('auth') || message.includes('login') || message.includes('sign-in')) {
        // Show auth errors - they're important for users
        return;
      }
      
      // Suppress other unknown errors
      if (message.includes('index') || 
          message.includes('permission') || 
          message.includes('network') || 
          message.includes('firestore') ||
          message.includes('unavailable') ||
          message.includes('timeout') ||
          message.includes('conversation') ||
          message.includes('messaging') ||
          message.includes('unexpected') ||
          message.includes('error') ||
          e.domain === 'unknown' ||
          e.domain === 'firestore') {
        secureLog('warn', 'Suppressed unknown error:', e.message);
        return;
      }
    }
  }

  // Suppress errors during initial page load (first 10 seconds)
  const pageLoadTime = performance.now();
  if (pageLoadTime < 10000) {
    // Suppress most errors during page load to prevent user confusion
    const isPageLoadSuppressible = (e.code as string) === 'DB/INDEX_REQUIRED' ||
                                  (e.code as string) === 'DB/PERMISSION_DENIED' ||
                                  (e.code as string) === 'TRANSIENT_BACKEND_UNAVAILABLE' ||
                                  e.code === ErrorCode.UNKNOWN ||
                                  e.domain === 'unknown' ||
                                  e.domain === 'firestore';
    
    if (isPageLoadSuppressible) {
      // SECURITY: Removed console.warn to prevent information leakage
      return;
    }
  }

  const fallback = { title: e.code, description: e.message ?? '' };
  const copy = ERROR_MESSAGES[e.code] ?? fallback;

  // If the error has a user-friendly message (like email verification), use it instead of the generic message
  // Check if the message is longer than the generic one or contains specific helpful keywords
  const hasUserFriendlyMessage = e.message && (
    e.message.length > 50 || // Long messages are usually user-friendly
    e.message.includes('verify your email') ||
    e.message.includes('check your inbox') ||
    e.message.includes('verification email') ||
    (e.message !== copy.description && e.message !== copy.title && e.message.length > 20)
  );
  
  const displayMessage = hasUserFriendlyMessage ? e.message : (copy.description || copy.title);

  // SECURITY: Sanitize error messages to prevent XSS
  const sanitizedMessage = sanitizeString(displayMessage, 500) || 'An error occurred';
  const sanitizedCopyDescription = sanitizeString(copy.description || copy.title, 500) || 'An error occurred';

  // dedupe identical errors briefly
  const key = `${e.code}:${e.domain}:${sanitizedMessage}`;
  const now = Date.now();
  const last = shownKeys.get(key) ?? 0;
  if (now - last < WINDOW_MS) return;
  shownKeys.set(key, now);

  if (e.severity === ErrorSeverity.Info) return;
  if (e.severity === ErrorSeverity.Warn || e.severity === ErrorSeverity.Error) {
    toast.error(sanitizedMessage);
    return;
  }
  if (e.severity === ErrorSeverity.Fatal) {
    // could open a modal if you have one
    toast.error(sanitizedCopyDescription);
  }
}

// Optional: Show error modal for severe errors
export function showErrorModal(err: AppError) {
  // This could be implemented with a modal library like react-modal
  // For now, we'll just use toast with longer duration
  if (err.code === 'UNKNOWN' || err.domain === 'unknown') {
    toast.error('An unexpected error occurred. Please try again or contact support.', { 
      duration: 8000 
    });
  }
}
